import { action, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import WorkspacePostingStore from 'admin/WorkspacePostingStore'
import {
  JobTypeOption,
  LocationOption,
  PrimarySoftwareOption,
  SpecialtyOption,
  SupplementalSoftwareOption,
  UpdateWorkspacePostingPayload,
} from 'admin/staffing/dtos/payloads'
import { AlertFactory, AlertState } from 'alerts/Alert'
import { GetCurrentUserWorkspaceMemberships } from 'iam/WorkspaceMembershipStore'
import { Id } from 'types/definitions'
import { WorkspacePostingDialogViewModel } from '../WorkspacePostingDialog/WorkspacePostingDialogViewModel'
import { extractMessageFromError } from 'utils/error-util'
import WorkspacePosting from 'admin/staffing/WorkspacePosting'

export default class UpdateWorkspacePostingViewModel extends WorkspacePostingDialogViewModel {
  constructor(
    private workspacePostingStore: WorkspacePostingStore,
    private newAlert: AlertFactory,
    private refreshStatusFilter: () => Promise<void>,
    getWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
  ) {
    super(getWorkspaceMemberships)
    makeObservable(this, {
      showWithData: action.bound,
      submit: action.bound,
    })
  }

  // TODO: Find a better solution for this type casting BS.
  /**
   * Show the update workspace posting dialog with populated fields.
   * @param workspacePosting
   */
  showWithData = (workspacePosting: WorkspacePosting) => {
    this.workspacePosting = workspacePosting
    this.workspace = workspacePosting.workspace
    this.location = workspacePosting.location as LocationOption
    this.primarySoftware =
      workspacePosting.primarySoftware as PrimarySoftwareOption
    this.backupRequired = workspacePosting.backupFileNeeded
    this.supplementalSoftware =
      workspacePosting.supplementalSoftware as SupplementalSoftwareOption
    this.specialties = workspacePosting.specialties as SpecialtyOption[]
    this.prosRequired = workspacePosting.capacity
    this.maxConcurrentWorkspaces = workspacePosting.maxAllowedWorkspaces
    this.typeOfWork = workspacePosting.jobTypes as JobTypeOption[]
    this.description = workspacePosting.description
    this.opportunityType = workspacePosting.opportunityType

    super.show()
  }

  /**
   * Update a workspace posting.
   */
  submit = task.resolved(async () => {
    if (!this.validate() || this.workspacePosting?.status !== 'DRAFT') {
      return
    }

    const id = Id.assert(this.workspacePosting.id)
    const payload: UpdateWorkspacePostingPayload = {
      location: this.location,
      primary_software: this.primarySoftware,
      backup_file_needed: this.backupRequired,
      supplemental_software: this.supplementalSoftware,
      specialties: this.specialties,
      capacity: this.prosRequired,
      max_allowed_workspaces: this.maxConcurrentWorkspaces,
      job_types: this.typeOfWork,
      description: this.description,
    }

    await this.workspacePostingStore
      .updateWorkspacePosting(id, payload)
      .then(() => {
        this.refreshStatusFilter()

        this.newAlert()
          .withState(AlertState.Success)
          .saying('Success', 'Workspace posting updated.')
          .autoDismiss()
          .show()

        this.close()
      })
      .catch((error) => {
        this.newAlert()
          .withState(AlertState.Error)
          .saying('Error', extractMessageFromError(error))
          .autoDismiss()
          .show()
      })
  })
}
